import {first} from 'lodash';
import {Logger} from '../utils/Logger';
import {Router} from '@angular/router';
import {DOCUMENT} from '@angular/common';
import {FormGroupUtil} from '../utils/FormGroupUtil';
import {ConfigService} from '../service/config.service';
import {ApiServiceService} from '../api-service.service';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {TooltipComponent} from '../tooltip/tooltip.component';
import {InvestmentType} from '../model/investment-type.model';
import {Keys, StorageService} from '../service/storage.service';
import {LenderDetailsService} from '../service/lender-details.service';
import {InvestmentNavigator} from '../utils/investment-navigator';
import {Component, Inject, OnInit, PLATFORM_ID, ViewChild} from '@angular/core';
import {LoanAgreementComponent, Result} from '../loanAgreement/loanAgreement.component';
import {AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA} from '@angular/material/dialog';

export enum ComponentState {
    Initializing, Ready
}

@Component({
    providers: [ApiServiceService],
    templateUrl: './create-portfolio.component.html',
    styleUrls: ['./create-portfolio.component.css']
})
export class CreatePortfolioComponent implements OnInit {
    public readonly formGroup: FormGroup;
    public readonly configService: ConfigService;
    public readonly ComponentState = ComponentState;
    public componentState = ComponentState.Initializing;

    private walletBalance: number;
    private principalOutstanding: number;
    private numOflenderPortfolios: number;
    private readonly document: any;
    private readonly router: Router;
    private readonly dialog: MatDialog;
    private readonly platformId: Object;
    private readonly storageService: StorageService;
    isPreFlexUser: boolean;
    @ViewChild(TooltipComponent)
    private readonly tooltipComponent: TooltipComponent;
    private readonly lenderDetailsService: LenderDetailsService;
    private readonly manualInvestNavigator: InvestmentNavigator;
    private readonly dialogReference: MatDialogRef<CreatePortfolioComponent>;

    constructor(configService: ConfigService, storageService: StorageService, lenderDetailsService: LenderDetailsService,
                formBuilder: FormBuilder, manualInvestNavigator: InvestmentNavigator, router: Router,
                dialogReference: MatDialogRef<CreatePortfolioComponent>, @Inject(PLATFORM_ID) platformId: Object,
                @Inject(DOCUMENT) document: any, dialog: MatDialog, 
                @Inject(MAT_DIALOG_DATA) data: {principalOutstanding: number, 
                    numOflenderPortfolios: number, walletAmount: number}) {
        this.router = router;
        this.dialog = dialog;
        this.document = document;
        this.platformId = platformId;
        this.configService = configService;
        this.storageService = storageService;
        this.dialogReference = dialogReference;
        this.lenderDetailsService = lenderDetailsService;
        this.manualInvestNavigator = manualInvestNavigator;
        this.numOflenderPortfolios = data.numOflenderPortfolios;
        this.walletBalance = data.walletAmount;
        this.principalOutstanding = data.principalOutstanding;
        this.isPreFlexUser = this.storageService.getItem(Keys.FINX) === "true" && this.storageService.getItem(Keys.IS_LENDER_CREATED_AFTER_RESTRICTED_DATE) === "true";
        this.formGroup = formBuilder.group({
            'portfolioAmount': formBuilder.control(null, [Validators.required, Validators.min(configService.getMinimumInvestmentLimit(),),
                this.isPreFlexUser  ? Validators.max(configService.getMaximumInvestmentLimitForFinx()) : Validators.required,
                this.principalOutstandingLimitValidator(), this.walletBalanceLimitValidator(), this.denomination()]),
            'consent': formBuilder.control(false, Validators.requiredTrue),
            'consent1': this.isPreFlexUser ? formBuilder.control(false, Validators.requiredTrue): formBuilder.control(null),
        });
    }

    public isWalletBalanceSufficient(): boolean {
        return this.isWalletBalanceSufficientForFirstPortfolioCreation() && this.isWalletBalanceSufficientForMinPortfolioAmount();
    }

    public isWalletBalanceSufficientForMinPortfolioAmount(): boolean{
        return this.walletBalance >= this.getMinimumPortfolioCreationAmount();
    }

    public isWalletBalanceSufficientForFirstPortfolioCreation(): boolean{
        if(this.numOflenderPortfolios === 0){
            if(this.walletBalance >= this.getMinimumWalletBalanceForInitialPortfolio()){
                return true;
            }
            return false;
        }
        return true;
    }

    public onWalletBalanceReceived(walletBalance: number): void {
        this.walletBalance = walletBalance;
    }

    public async ngOnInit(): Promise<void> {
        const lenderId = this.storageService.getItem(Keys.LENDER_ID);
        await Promise.all([this.refreshWalletAmount(lenderId), this.refreshPrincipalOutstanding(lenderId)]);
        this.componentState = ComponentState.Ready;
    }

    public getMinimumPortfolioCreationAmount(): number {
        return this.configService.getMinimumInvestmentLimit();
    }

    public getInvestmentDenomination(): number {
        return this.configService.getDenomination();
    }

    public getWalletBalance(): number {
        return this.walletBalance;
    }

    public onManualInvestOptionClicked(formGroup: FormGroup): void {
        FormGroupUtil.validateFormGroup(formGroup);
        if (formGroup.invalid) {
            Logger.log(`Form is invalid. Not proceeding with the request.`);
            return;
        }
        this.showLoanAgreement(InvestmentType.Manual, formGroup.controls.consent.value, formGroup.controls.portfolioAmount.value);
    }

    public onFinzyProOptionClicked(formGroup: FormGroup): void {
        FormGroupUtil.validateFormGroup(formGroup);
        if (formGroup.invalid) {
            Logger.log(`Form is invalid. Not proceeding with the request.`);
            return;
        }
        this.showLoanAgreement(InvestmentType.Auto, formGroup.controls.consent.value, formGroup.controls.portfolioAmount.value, this.isPreFlexUser ? formGroup.controls.consent1.value : null);
    }

    public getErrorMessage(control: AbstractControl): string {
        const controlName = this.getControlName(control);
        const errors = this.allErrorMessages();
        const message = '';
        if (control.errors) {
            const occurredError = first(Object.keys(control.errors));
            const predefinedErrorsForControl = errors[controlName];
            const result = predefinedErrorsForControl.find(predefinedError => {
                return predefinedError.type === occurredError;
            });
            if (result) {
                return result.message;
            } else {
                Logger.log(`Could not find error message for error: ${occurredError}`);
            }
        }
        return message;
    }

    public getAmountAvailableToInvest(): number {
        const amountTillPrincipalOutstandingLimit = this.getAmountTillPrincipalOutstandingLimit();
        return Math.min(this.getWalletBalance(), amountTillPrincipalOutstandingLimit);
    }

    public onPrincipalOutstandingReceived(principalOutstanding: number): void {
        this.principalOutstanding = principalOutstanding;
    }

    public goToWallet(): void {
        this.dialogReference.close();
        this.router.navigate(['investorwallet']);
    }

    public getAmountRemainingToInvest(enteredAmount: any): number {
        return Math.max(0, this.getAmountAvailableToInvest() - enteredAmount);
    }

    public getPrincipalOutstanding(): number {
        return this.principalOutstanding;
    }

    public showLoanAgreement(investmentType: InvestmentType, consent: boolean, portfolioAmount: number, consent1?: boolean): void {
        const dialogRef = this.dialog.open(LoanAgreementComponent);
        dialogRef.afterClosed().subscribe((result) => this.onLoanAgreementResultReceived(result, investmentType, consent, portfolioAmount, consent1));
    }

    public onLoanAgreementResultReceived(result: any, investmentType: InvestmentType, consent: boolean, portfolioAmount: number, consent1?: boolean): void {
        if (result === Result.ACCEPTED) {
            this.storageService.saveObjectToSession(Keys.LENDER_10LAKH_LIMIT_CHECK, consent);
            (this.isPreFlexUser) && this.storageService.saveObjectToSession(Keys.FINZY_FLEX_PRE_LAUNCH_AGGREMENT, consent1);
            switch (investmentType) {
                case InvestmentType.Auto:
                    this.manualInvestNavigator.navigateToAutoInvest(portfolioAmount.toString());
                    break;
                case InvestmentType.Manual:
                    this.manualInvestNavigator.navigateToManualInvestListingPage(portfolioAmount.toString());
                    break;
                default:
                    Logger.log('I don\'t know how I got here. :|');
            }
            this.dialogReference.close();
        }
    }

    public getInvestmentAmountInfoMessage(): string {
        const walletBalance = this.getWalletBalance();
        const fiftyPercentOfWalletAmount = (walletBalance - (walletBalance * 50 / 100));
        if (this.isValidInvestmentAmount() && this.isInvestmentAmountIsInTermsOfThousands() && this.isInvestmentAmountIsGreaterThanMinimumLimit() &&
            this.isInvestmentAmountIsLessThanFiftyThousandLimit() && this.formGroup.controls.portfolioAmount.value < fiftyPercentOfWalletAmount) {
            return 'Creating smaller portfolios increases diversification, but it also increases the time your money ' +
                'has to wait in queue to invest in new loans. Consider creating a portfolio of ' + fiftyPercentOfWalletAmount +
                ' or more.';
        }
        return '';
    }

    private isValidInvestmentAmount() {
        return !this.formGroup.controls.portfolioAmount.invalid;
    }

    private isInvestmentAmountIsGreaterThanMinimumLimit() {
        return this.formGroup.controls.portfolioAmount.value >= 5000;
    }

    private isInvestmentAmountIsLessThanFiftyThousandLimit() {
        return this.formGroup.controls.portfolioAmount.value <= 50000;
    }

    private isInvestmentAmountIsInTermsOfThousands() {
        return this.formGroup.controls.portfolioAmount.value &&
            this.formGroup.controls.portfolioAmount.value.toString().length > 3;
    }

    private allErrorMessages() {
        return {
            portfolioAmount: [{
                type: 'required',
                message: 'Please provide the portfolio amount'
            }, {
                type: 'principalOutstanding',
                message: `Amount breaches the principal outstanding limit`
            }, {
                type: 'walletBalance',
                message: `Amount is more than wallet balance`
            }, {
                type: 'denomination',
                message: `Please enter an amount in the multiples of ₹${this.configService.getDenomination()}`
            }, {
                type: 'min',
                message: `Minimum portfolio amount is ₹${this.configService.getMinimumInvestmentLimit()}`
            }, this.isPreFlexUser ? {
                type: 'max',
                message: `Maximum portfolio amount is ₹${this.configService.getMaximumInvestmentLimitForFinx()}`
            } : {}
        ]
        };
    }

    private getAmountTillPrincipalOutstandingLimit() {
        return this.configService.getMaximumInvestmentLimit() - this.principalOutstanding;
    }

    private async refreshPrincipalOutstanding(lenderId) {
        this.onPrincipalOutstandingReceived(this.principalOutstanding);
    }

    private async refreshWalletAmount(lenderId) {
        this.onWalletBalanceReceived(this.walletBalance);
    }

    private getControlName(c: AbstractControl): string | null {
        const formGroup = c.parent.controls;
        return Object.keys(formGroup).find(name => c === formGroup[name]) || null;
    }

    private principalOutstandingLimitValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const validationErrors = {
                principalOutstanding: 'Amount exceeds the principal outstanding limit'
            };
            return control.value <= this.getAmountTillPrincipalOutstandingLimit() ? null : validationErrors;
        };
    }

    private walletBalanceLimitValidator() {
        return (control: AbstractControl): ValidationErrors | null => {
            const validationErrors = {
                walletBalance: 'Amount exceeds the wallet balance limit',
            };
            return control.value <= this.getWalletBalance() ? null : validationErrors;
        };
    }

    private denomination(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const denomination = this.configService.getDenomination();
            return control.value % denomination === 0 ? null : {denomination: true};
        };
    }

    public getMinimumWalletBalanceForInitialPortfolio(): number {
        return ConfigService.getMinimumWalletBalanceForInitialPortfolio();
    }

    public getInvestorNetworthCertificateUrlByName(fileName): string {
        return this.configService.getPolicyFileUrlByName(fileName);
    }
}
